/**
 * @module app.um
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { ExpandEvent } from 'app/shared/elements/ess-table';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class AuthMedicationsStatusHistoryByMedication extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'reload' })
    protected authId: string | null = null;

    protected authMedications: UM.AuthMedication[] = [];
    protected authMedicationStatusHistoryDetail: Record<string, boolean> = {};

    private sorting = new Sorting();
    private defaultSorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 10;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        ...rest: any[]) {
        super(...rest);

        this.defaultSorting.entries = [
            {
                reference: 'definition.code',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    protected authMedicationHistoryLoaded(event: ExpandEvent<UM.AuthMedication>): void {
        const item = event.detail.item;
        const items = event.detail.items;

        if(item && items.includes(item)) {
            this.authMedicationStatusHistoryDetail[item.id!] = true;
        }
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadAuthMedications();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadAuthMedications(): Promise<void> {
        if(!this.authId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting.entries.length ? this.sorting : this.defaultSorting,
            includeInactive: true
        };

        const includes = [
            'definition'
        ];

        const fields = [
            'definition.code',
            'definition.description',
            'sequence'
        ];


        try {
            this.state.beginLoad();

            const response = await this.service.loadMedicationsByAuth(this.authId, options, includes, fields);
            this.authMedications = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
